import { ActionTree, Commit } from 'vuex';
import { PipelineState } from './state';
import RootState from '@/store/root_state';
import pipeline from '@/pipeline';
// eslint-disable-next-line import/no-cycle
import router from '@/router';
import { MicroserviceEnum, mutation, query } from '@/helpers/westeros';
import {
  getAdmissionPipelineRun,
  GetAdmissionPipelineRunQueryResult,
} from '@/store/graphql/queries/westerosQueries';
import {
  upsertAdmissionPipelineRun,
  UpsertAdmissionPipelineRunMutationResult,
} from '@/store/graphql/mutations/westerosMutations';
import { Context } from '@/pipeline/stateMachine';
import { State } from 'xstate';
import Vue from 'vue';
import { AnalyticsActions } from '../analytics/actions';

// Fila de escrita do `PipelineRun`, em escopo de módulo porque o listener é registrado
// uma vez por sessão e precisa serializar as transições entre si. Sem isso as escritas
// eram fire-and-forget (o `onTransition` do XState ignora a promise do listener) e, com o
// `RetryLink` ativo, duas transições rápidas podiam gravar o estado mais ANTIGO por
// último.
let writeQueue: Promise<void> = Promise.resolve();

// Contador monotônico: escrita cuja sequência já foi superada é descartada. O backend
// guarda posição, não trilha — só o estado mais novo importa.
let lastEnqueuedSequence = 0;

// A fila e o contador são estado de módulo e vazam entre testes: sem reset explícito o
// descarte por sequência engole escritas de specs posteriores.
export function resetPipelineRunWriteQueue(): void {
  writeQueue = Promise.resolve();
  lastEnqueuedSequence = 0;
}

// Devolve a cadeia atual para que o teste possa esperar o esvaziamento da fila.
export function flushPipelineRunWriteQueue(): Promise<void> {
  return writeQueue;
}

function enqueuePipelineRunWrite(
  commit: Commit,
  admissionId: number | undefined,
  currentState: State<Context>,
): void {
  // O parâmetro é `$admissionId: Float!` e o `forceTrigger` do listener dispara em `/`,
  // antes de existir admissão. Pular em silêncio: a transição seguinte persiste.
  if (!admissionId) return;

  lastEnqueuedSequence += 1;
  const sequence = lastEnqueuedSequence;

  writeQueue = writeQueue
    .then(async () => {
      // Coalescing: já existe escrita mais nova enfileirada, esta virou irrelevante.
      if (sequence !== lastEnqueuedSequence) return;

      const { data } = await mutation<UpsertAdmissionPipelineRunMutationResult>(
        MicroserviceEnum.ADMISSIONS,
        upsertAdmissionPipelineRun,
        { admissionId, state: currentState },
      );

      if (!data?.upsertAdmissionPipelineRun) return;

      // O selection set da mutation é `{ id }` apenas: `state` volta SEMPRE `undefined`.
      // Commitar o da resposta zerava `currentState` a cada transição — justamente o
      // valor que o `START_PIPELINE` lê para restaurar o pipeline. Commitamos o estado
      // que acabamos de enviar.
      const pipelineState: PipelineState = {
        id: data.upsertAdmissionPipelineRun.id,
        currentState,
      };

      commit('SET_PIPELINE', pipelineState);
    })
    .catch(() => {
      // A cadeia NUNCA pode rejeitar: um único reject não tratado envenena a fila e mata
      // toda a persistência da sessão.
    });
}

const actions: ActionTree<PipelineState, RootState> = {
  async START_PIPELINE({ commit, state, rootState, dispatch }): Promise<void> {
    pipeline.start(router, state.currentState);

    // O listener é síncrono de propósito: o `onTransition` do XState ignora a promise
    // devolvida, então ele apenas enfileira e a ordenação fica com a fila.
    pipeline.addTransitionListener((currentState: State<Context>) => {
      dispatch(AnalyticsActions.SCREEN_VIEWED);

      enqueuePipelineRunWrite(commit, rootState.admission.admissionId, currentState);
    }, true);
  },

  async PULL_PIPELINE({ commit, rootState }): Promise<void> {
    const isAuthenticated = Boolean(Vue.$cookies.get('admissions-auth-token'));

    if (!isAuthenticated) return;

    const variables = { admissionId: rootState.admission.admissionId };

    const { data } = await query<GetAdmissionPipelineRunQueryResult>(
      MicroserviceEnum.ADMISSIONS,
      getAdmissionPipelineRun,
      variables,
    ).catch(() => ({ data: null }));

    if (data?.getAdmissionPipelineRun) {
      const pipelineState: PipelineState = {
        id: data.getAdmissionPipelineRun.id,
        currentState: data.getAdmissionPipelineRun.state,
      };

      commit('SET_PIPELINE', pipelineState);
    }
  },
};

export default actions;
